/**
 * ExperienceMontrealCarousel — home page section.
 *
 * Pulls approved partners from /api/experiences and renders one
 * horizontal scrollable strip per category (skipping empty categories
 * on the home page — the dedicated /experiences page surfaces those
 * via "Coming soon" placeholders).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Utensils, Car, Compass, Anchor, Bike, Camera, MapPin as MapPinIcon, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = '#1E3F70';
const PINK = '#FF5C8A';

const CATEGORY_META = {
  restaurant:         { label: 'Restaurants',  icon: Utensils },
  car_rental:         { label: 'Car Rental',   icon: Car },
  car_rides:          { label: 'Drivers',      icon: Car },
  activities:         { label: 'Activities',   icon: Compass },
  boat_rental:        { label: 'Boat Rental',  icon: Anchor },
  bike_rental:        { label: 'Bike Rental',  icon: Bike },
  photography:        { label: 'Photography',  icon: Camera },
  tour_guide:         { label: 'Tour Guides',  icon: MapPinIcon },
  cleaning_concierge: { label: 'Concierge',    icon: Sparkles },
};

const ExperienceMontrealCarousel = () => {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/experiences`)
      .then((r) => !cancelled && setExperiences(r.data.experiences || []))
      .catch(() => !cancelled && setExperiences([]))
      .finally(() => !cancelled && setReady(true));
    return () => { cancelled = true; };
  }, []);

  // Bucket experiences by primary category.
  const byCategory = experiences.reduce((acc, e) => {
    const primary = (e.service_categories || [])[0];
    if (primary) (acc[primary] = acc[primary] || []).push(e);
    return acc;
  }, {});

  // On the home page we hide empty categories — guests can browse the
  // full scope (incl. Coming soon) by clicking through to /experiences.
  const liveCategories = Object.keys(byCategory).filter((k) => byCategory[k].length > 0);

  // Hide the section entirely when there are no approved partners at
  // all — avoids a hollow "Experience Montreal" heading on day 0.
  if (ready && liveCategories.length === 0) return null;

  return (
    <section className="py-16 bg-white" data-testid="home-experience-montreal">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] mb-2" style={{ color: PINK }}>
              Experience Montreal
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              Eat, ride, explore
            </h2>
            <p className="text-base text-gray-600 mt-2 max-w-2xl">
              Curated restaurants, rentals and activities — chosen by our team and trusted by our guests.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/experiences')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: NAVY }}
            data-testid="home-experience-see-all"
          >
            See all experiences →
          </button>
        </div>

        {/* Per-category strips */}
        <div className="space-y-10">
          {liveCategories.map((slug) => {
            const meta = CATEGORY_META[slug] || { label: slug, icon: Sparkles };
            const items = byCategory[slug];
            const Ico = meta.icon;
            return (
              <div key={slug} data-testid={`home-experience-row-${slug}`}>
                {/* Category sub-heading */}
                <div className="flex items-center gap-3 mb-3">
                  <span aria-hidden className="block w-8 h-[2px] rounded-full" style={{ backgroundColor: PINK }} />
                  <Ico className="w-4 h-4" style={{ color: NAVY }} strokeWidth={2} />
                  <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-gray-900">{meta.label}</h3>
                </div>

                {/* Horizontal scroll strip */}
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
                  {items.map((exp) => (
                    <button
                      key={exp.id}
                      type="button"
                      onClick={() => navigate(`/experiences/${exp.slug}`)}
                      className="group flex-shrink-0 w-64 text-left rounded-xl overflow-hidden bg-white ring-1 ring-gray-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 snap-start"
                      data-testid={`home-experience-card-${exp.slug}`}
                    >
                      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                        {exp.cover_image ? (
                          <img src={exp.cover_image} alt={exp.business_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white" style={{ background: `linear-gradient(135deg, ${NAVY}, ${PINK})` }}>
                            <Sparkles className="w-8 h-8 opacity-75" />
                          </div>
                        )}
                        {exp.price_tier && (
                          <span
                            className="absolute top-2 right-2 text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: '#fff', color: '#1F5C42' }}
                          >
                            {exp.price_tier}
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-1">{exp.business_name}</h4>
                        {exp.city && (
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                            <MapPinIcon className="w-3 h-3" /> {exp.city}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExperienceMontrealCarousel;
